import React, { useState, useRef, useCallback } from 'react';
import { completedProjectService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Upload, Trash2, Edit2, Check, X, AlertCircle, Loader2,
  Eye, EyeOff, Play, Youtube, Video, Plus, Link2, Tag,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Helpers ───────────────────────────────────────────────────
const getYoutubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const getYoutubeThumbnail = (url) => {
  const id = getYoutubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};

// ── Upload / Create Modal ────────────────────────────────────
const CreateModal = ({ onClose, onCreated }) => {
  const [source,      setSource]      = useState('YOUTUBE'); // 'YOUTUBE' | 'UPLOAD'
  const [title,       setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [youtubeUrl,  setYoutubeUrl]  = useState('');
  const [clientName,  setClientName]  = useState('');
  const [completedAt, setCompletedAt] = useState('');
  const [tagInput,    setTagInput]    = useState('');
  const [tags,        setTags]        = useState([]);
  const [videoFile,   setVideoFile]   = useState(null);
  const [thumbFile,   setThumbFile]   = useState(null);
  const [uploading,   setUploading]   = useState(false);
  const videoRef = useRef();
  const thumbRef = useRef();

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput('');
  };

  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error('Please enter a title'); return; }
    if (source === 'YOUTUBE' && !youtubeUrl.trim()) { toast.error('Please enter a YouTube URL'); return; }
    if (source === 'YOUTUBE' && !getYoutubeId(youtubeUrl)) { toast.error('Invalid YouTube URL'); return; }
    if (source === 'UPLOAD' && !videoFile) { toast.error('Please select a video file'); return; }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('videoSource', source);
      fd.append('title', title.trim());
      if (description.trim()) fd.append('description', description.trim());
      if (clientName.trim())  fd.append('clientName',  clientName.trim());
      if (completedAt)        fd.append('completedAt', completedAt);
      if (tags.length > 0)    fd.append('tags', JSON.stringify(tags));
      if (source === 'YOUTUBE') fd.append('youtubeUrl', youtubeUrl.trim());
      if (source === 'UPLOAD' && videoFile) fd.append('video', videoFile);
      if (thumbFile) fd.append('thumbnail', thumbFile);

      await completedProjectService.create(fd);
      toast.success('Project added successfully');
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create project');
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-cream-200">
          <h2 className="font-serif font-semibold text-xl text-forest-900">Add Completed Project</h2>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700 transition-colors"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-5">

          {/* Video source toggle */}
          <div>
            <label className="block text-sm font-medium text-forest-700 mb-2">Video Source</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setSource('YOUTUBE')}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                  source === 'YOUTUBE'
                    ? 'border-red-500 bg-red-50 text-red-600'
                    : 'border-cream-200 text-forest-600 hover:border-cream-300'
                }`}
              >
                <Youtube size={18} /> YouTube Link
              </button>
              <button
                onClick={() => setSource('UPLOAD')}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                  source === 'UPLOAD'
                    ? 'border-forest-600 bg-forest-50 text-forest-700'
                    : 'border-cream-200 text-forest-600 hover:border-cream-300'
                }`}
              >
                <Video size={18} /> Upload Video
              </button>
            </div>
          </div>

          {/* YouTube URL */}
          {source === 'YOUTUBE' && (
            <div>
              <label className="block text-sm font-medium text-forest-700 mb-1.5">YouTube URL <span className="text-rust-500">*</span></label>
              <div className="flex items-center gap-2 border border-cream-300 rounded-xl px-3 focus-within:ring-2 focus-within:ring-forest-500 focus-within:border-transparent">
                <Link2 size={15} className="text-forest-400 shrink-0" />
                <input
                  type="url"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="flex-1 py-2.5 text-sm text-forest-800 outline-none bg-transparent"
                />
              </div>
              {youtubeUrl && getYoutubeId(youtubeUrl) && (
                <div className="mt-2 rounded-xl overflow-hidden aspect-video max-w-[200px]">
                  <img src={getYoutubeThumbnail(youtubeUrl)} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          )}

          {/* Video upload */}
          {source === 'UPLOAD' && (
            <div>
              <label className="block text-sm font-medium text-forest-700 mb-1.5">Video File <span className="text-rust-500">*</span></label>
              <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={(e) => setVideoFile(e.target.files[0])} />
              <button
                onClick={() => videoRef.current.click()}
                className="w-full border-2 border-dashed border-cream-300 rounded-xl py-6 flex flex-col items-center gap-2 text-forest-500 hover:border-forest-400 hover:bg-forest-50 transition-all"
              >
                <Video size={28} />
                {videoFile
                  ? <span className="text-sm text-forest-700 font-medium">{videoFile.name}</span>
                  : <span className="text-sm">Click to select a video (mp4, webm, mov…)</span>
                }
              </button>
            </div>
          )}

          {/* Thumbnail (optional for both) */}
          <div>
            <label className="block text-sm font-medium text-forest-700 mb-1.5">
              Thumbnail Image <span className="text-forest-400 font-normal">(optional)</span>
            </label>
            <input ref={thumbRef} type="file" accept="image/*" className="hidden" onChange={(e) => setThumbFile(e.target.files[0])} />
            <button
              onClick={() => thumbRef.current.click()}
              className="w-full border-2 border-dashed border-cream-300 rounded-xl py-4 flex items-center justify-center gap-2 text-sm text-forest-500 hover:border-forest-400 hover:bg-forest-50 transition-all"
            >
              <Upload size={16} />
              {thumbFile ? thumbFile.name : 'Upload custom thumbnail (jpg, png, webp)'}
            </button>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-forest-700 mb-1.5">Title <span className="text-rust-500">*</span></label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Adwoa's Eco-Lodge Launch"
              className="w-full border border-cream-300 rounded-xl px-4 py-2.5 text-sm text-forest-800 outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-forest-700 mb-1.5">Description <span className="text-forest-400 font-normal">(optional)</span></label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Brief summary of the project..."
              className="w-full border border-cream-300 rounded-xl px-4 py-2.5 text-sm text-forest-800 outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Client name + completed date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-forest-700 mb-1.5">Client / Business Name</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="e.g. Kojo Mensah"
                className="w-full border border-cream-300 rounded-xl px-4 py-2.5 text-sm text-forest-800 outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-forest-700 mb-1.5">Date Completed</label>
              <input
                type="date"
                value={completedAt}
                onChange={(e) => setCompletedAt(e.target.value)}
                className="w-full border border-cream-300 rounded-xl px-4 py-2.5 text-sm text-forest-800 outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-forest-700 mb-1.5">Tags <span className="text-forest-400 font-normal">(optional)</span></label>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="e.g. Tourism, Hospitality…"
                className="flex-1 border border-cream-300 rounded-xl px-4 py-2.5 text-sm text-forest-800 outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
              />
              <button onClick={addTag} className="btn-outline px-4 py-2.5 text-sm">Add</button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((t) => (
                  <span key={t} className="flex items-center gap-1.5 bg-cream-100 text-forest-700 text-xs px-3 py-1 rounded-full border border-cream-200">
                    {t}
                    <button onClick={() => removeTag(t)} className="text-forest-400 hover:text-rust-500 transition-colors"><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-cream-200">
          <button onClick={onClose} className="btn-outline px-6 py-2.5 text-sm" disabled={uploading}>Cancel</button>
          <button onClick={handleSubmit} disabled={uploading} className="btn-primary px-6 py-2.5 text-sm">
            {uploading
              ? <><Loader2 size={14} className="animate-spin" /> Uploading…</>
              : <><Plus size={14} /> Add Project</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Edit Modal ────────────────────────────────────────────────
const EditModal = ({ project, onClose, onUpdated }) => {
  const [title,       setTitle]       = useState(project.title);
  const [description, setDescription] = useState(project.description || '');
  const [clientName,  setClientName]  = useState(project.clientName || '');
  const [completedAt, setCompletedAt] = useState(
    project.completedAt ? project.completedAt.slice(0, 10) : ''
  );
  const [tagInput, setTagInput] = useState('');
  const [tags,     setTags]     = useState(
    project.tags ? (typeof project.tags === 'string' ? JSON.parse(project.tags) : project.tags) : []
  );
  const [saving, setSaving] = useState(false);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput('');
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await completedProjectService.update(project.id, {
        title: title.trim(),
        description: description.trim() || null,
        clientName: clientName.trim() || null,
        completedAt: completedAt || null,
        tags,
      });
      toast.success('Project updated');
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-cream-200">
          <h2 className="font-serif font-semibold text-lg text-forest-900">Edit Project</h2>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-forest-700 mb-1.5">Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-cream-300 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-forest-700 mb-1.5">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
              className="w-full border border-cream-300 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-forest-700 mb-1.5">Client Name</label>
              <input type="text" value={clientName} onChange={(e) => setClientName(e.target.value)}
                className="w-full border border-cream-300 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-forest-700 mb-1.5">Date Completed</label>
              <input type="date" value={completedAt} onChange={(e) => setCompletedAt(e.target.value)}
                className="w-full border border-cream-300 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-forest-700 mb-1.5">Tags</label>
            <div className="flex gap-2">
              <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Add tag…"
                className="flex-1 border border-cream-300 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent" />
              <button onClick={addTag} className="btn-outline px-4 py-2.5 text-sm">Add</button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((t) => (
                  <span key={t} className="flex items-center gap-1.5 bg-cream-100 text-forest-700 text-xs px-3 py-1 rounded-full border border-cream-200">
                    {t}
                    <button onClick={() => setTags(tags.filter((x) => x !== t))} className="text-forest-400 hover:text-rust-500 transition-colors"><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-cream-200">
          <button onClick={onClose} className="btn-outline px-5 py-2.5 text-sm" disabled={saving}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary px-5 py-2.5 text-sm">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Check size={14} /> Save</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Project Row / Card ────────────────────────────────────────
const ProjectRow = ({ project, onEdit, onToggle, onDelete }) => {
  const isYoutube = project.videoSource === 'YOUTUBE';
  const thumb     = project.thumbnailUrl ||
    (isYoutube ? getYoutubeThumbnail(project.youtubeUrl) : null);
  const tags      = project.tags
    ? (typeof project.tags === 'string' ? JSON.parse(project.tags) : project.tags)
    : [];

  return (
    <div className="bg-white rounded-2xl border border-cream-200 overflow-hidden flex flex-col sm:flex-row gap-0">
      {/* Thumbnail */}
      <div className="w-full sm:w-36 h-28 sm:h-auto bg-forest-900 flex-shrink-0 relative overflow-hidden">
        {thumb
          ? <img src={thumb} alt={project.title} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Play size={24} className="text-cream-400/40" /></div>
        }
        <div className="absolute top-2 left-2">
          {isYoutube
            ? <span className="bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">YT</span>
            : <span className="bg-forest-700 text-cream-50 text-[9px] font-bold px-1.5 py-0.5 rounded">MP4</span>
          }
        </div>
      </div>

      {/* Info */}
      <div className="flex-1 px-5 py-4 flex flex-col justify-between gap-3">
        <div>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-forest-900 text-sm leading-snug">{project.title}</h3>
              {project.clientName && <p className="text-xs text-rust-500 mt-0.5">{project.clientName}</p>}
            </div>
            <span className={`text-[11px] font-semibold px-2 py-1 rounded-full shrink-0 ${
              project.isPublished ? 'bg-green-50 text-green-700' : 'bg-cream-100 text-forest-500'
            }`}>
              {project.isPublished ? 'Published' : 'Hidden'}
            </span>
          </div>
          {project.description && (
            <p className="text-xs text-forest-500 mt-1.5 line-clamp-2">{project.description}</p>
          )}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {tags.map((t) => (
                <span key={t} className="text-[10px] bg-cream-50 text-forest-600 px-2 py-0.5 rounded-full border border-cream-200">{t}</span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => onEdit(project)} className="flex items-center gap-1.5 text-xs font-medium text-forest-600 hover:text-forest-900 border border-cream-200 rounded-lg px-3 py-1.5 hover:bg-cream-50 transition-all">
            <Edit2 size={12} /> Edit
          </button>
          <button onClick={() => onToggle(project)} className="flex items-center gap-1.5 text-xs font-medium text-forest-600 hover:text-forest-900 border border-cream-200 rounded-lg px-3 py-1.5 hover:bg-cream-50 transition-all">
            {project.isPublished ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Publish</>}
          </button>
          <button onClick={() => onDelete(project)} className="flex items-center gap-1.5 text-xs font-medium text-rust-500 hover:text-rust-700 border border-rust-200 rounded-lg px-3 py-1.5 hover:bg-rust-50 transition-all">
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Admin Page ────────────────────────────────────────────
const AdminCompletedProjectsPage = () => {
  const { data, loading, error, refetch: refresh } = useApi(
    () => completedProjectService.getAllAdmin()
  );

  const [showCreate, setShowCreate] = useState(false);
  const [editing,    setEditing]    = useState(null);

  const projects = data?.projects || [];

  // Debug — remove after confirming it works
  console.log('[CompletedProjects] data:', data, 'error:', error);

  const handleToggle = async (project) => {
    try {
      await completedProjectService.update(project.id, { isPublished: !project.isPublished });
      toast.success(project.isPublished ? 'Project hidden' : 'Project published');
      refresh();
    } catch { toast.error('Failed to update visibility'); }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Delete "${project.title}"? This cannot be undone.`)) return;
    try {
      await completedProjectService.delete(project.id);
      toast.success('Project deleted');
      refresh();
    } catch { toast.error('Failed to delete project'); }
  };

  return (
    <DashboardLayout title="Completed Projects">
      <div className="space-y-6 max-w-4xl">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-2xl font-semibold text-forest-900">Completed Projects</h1>
            <p className="text-forest-500 text-sm mt-1">
              Manage project showcase videos visible on the public site.
            </p>
          </div>
          <button onClick={() => setShowCreate(true)} className="btn-primary text-sm px-5 py-2.5">
            <Plus size={15} /> Add Project
          </button>
        </div>

        {/* Stats strip */}
        <div className="flex gap-4 flex-wrap">
          <div className="bg-cream-50 border border-cream-200 rounded-xl px-5 py-3 text-center min-w-[100px]">
            <div className="text-2xl font-serif font-semibold text-forest-900">{projects.length}</div>
            <div className="text-xs text-forest-500 mt-0.5">Total</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-3 text-center min-w-[100px]">
            <div className="text-2xl font-serif font-semibold text-green-700">{projects.filter(p => p.isPublished).length}</div>
            <div className="text-xs text-green-600 mt-0.5">Published</div>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl px-5 py-3 text-center min-w-[100px]">
            <div className="text-2xl font-serif font-semibold text-red-600">{projects.filter(p => p.videoSource === 'YOUTUBE').length}</div>
            <div className="text-xs text-red-500 mt-0.5">YouTube</div>
          </div>
          <div className="bg-forest-50 border border-forest-100 rounded-xl px-5 py-3 text-center min-w-[100px]">
            <div className="text-2xl font-serif font-semibold text-forest-700">{projects.filter(p => p.videoSource === 'UPLOAD').length}</div>
            <div className="text-xs text-forest-500 mt-0.5">Uploaded</div>
          </div>
        </div>

        {/* List */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="text-forest-600 animate-spin" />
          </div>
        )}

        {error && (
          <div className="flex items-center gap-3 bg-rust-50 border border-rust-200 text-rust-700 px-5 py-4 rounded-xl">
            <AlertCircle size={18} /> Failed to load projects.
          </div>
        )}

        {!loading && !error && projects.length === 0 && (
          <div className="text-center py-20">
            <Play size={40} className="text-forest-300 mx-auto mb-3" />
            <p className="text-forest-500 text-sm">No projects yet. Click "Add Project" to get started.</p>
          </div>
        )}

        {!loading && !error && projects.length > 0 && (
          <div className="space-y-3">
            {projects.map((p) => (
              <ProjectRow
                key={p.id}
                project={p}
                onEdit={setEditing}
                onToggle={handleToggle}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateModal onClose={() => setShowCreate(false)} onCreated={refresh} />
      )}
      {editing && (
        <EditModal project={editing} onClose={() => setEditing(null)} onUpdated={refresh} />
      )}
    </DashboardLayout>
  );
};

export default AdminCompletedProjectsPage;